import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BookOpen, ChevronRight, Plus, Loader2 } from "lucide-react";
import { MemoryBookStatusBadge, RoadTo2027Badge } from "@/components/MemoryBookStatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "requested", label: "Demande reçue" },
  { value: "data_ready", label: "Snapshot prêt" },
  { value: "admin_review", label: "En relecture" },
  { value: "copf_review", label: "Soumis COPF" },
  { value: "finalized", label: "Terminé" },
  { value: "copf_approved", label: "Validé COPF" },
];

export default function AdminMemoryBooksPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = async (st = status) => {
    setLoading(true);
    try {
      const params = {};
      if (st && st !== "all") params.status = st;
      const res = await api.get("/admin/memory-books/requests", { params });
      setItems(res.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(status); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-memory-books-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold">Atelier Livrets AITO</h1>
          <p className="text-sm text-muted-foreground">
            Demandes reçues, snapshots, rédaction IA, PDF premium.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-56" data-testid="admin-memorybooks-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="admin-memorybooks-new-btn"
            className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau livret
          </Button>
        </div>
      </div>

      <NewBookDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(reqId) => {
          setCreateOpen(false);
          refresh();
          nav(`/admin/memoires/${reqId}`);
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            Demandes ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aucune demande dans ce statut.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-memorybooks-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Entité</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Période</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Demandé le</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {it.entity_name_snapshot || "—"}
                          {it.road_to_2027 && <RoadTo2027Badge />}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-wide">{it.memory_type}</TableCell>
                      <TableCell className="text-xs font-mono">{it.period_year || "—"}</TableCell>
                      <TableCell><MemoryBookStatusBadge status={it.status} /></TableCell>
                      <TableCell className="text-xs font-mono">
                        {new Date(it.created_at).toLocaleDateString("fr-FR")}
                      </TableCell>
                      <TableCell className="text-xs font-mono">v{it.latest_version_number || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          data-testid={`admin-memorybook-open-${it.id}`}
                        >
                          <Link to={`/admin/memoires/${it.id}`}>
                            Ouvrir <ChevronRight className="h-4 w-4 ml-1" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

// ---------------------------------------------------------------------------
// V11.20 — Dialog "Nouveau livret" (admin any entity)
// ---------------------------------------------------------------------------
const MEMORY_TYPES = [
  { value: "ATHLETE", label: "Athlète", entityType: "athlete", needsEntity: true },
  { value: "CLUB", label: "Club", entityType: "club", needsEntity: true },
  { value: "FEDERATION", label: "Fédération", entityType: "federation", needsEntity: true },
  { value: "HISTORICAL_EVENT", label: "Fait historique sportif", entityType: "historical_event", needsEntity: false, needsFreeText: true },
  { value: "PACIFIC_GAMES_2027", label: "Jeux du Pacifique 2027 (post-jeux)", entityType: "pacific_games", needsEntity: false },
  { value: "YEARBOOK", label: "Général (bilan AITO.PF)", entityType: "general", needsEntity: false },
];

function NewBookDialog({ open, onOpenChange, onCreated }) {
  const [memoryType, setMemoryType] = useState("ATHLETE");
  const [entityQuery, setEntityQuery] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [freeTextTitle, setFreeTextTitle] = useState("");   // V11.21 HISTORICAL_EVENT
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const meta = MEMORY_TYPES.find((m) => m.value === memoryType) || MEMORY_TYPES[0];

  useEffect(() => {
    setSelectedEntity(null);
    setEntityOptions([]);
    setEntityQuery("");
    setFreeTextTitle("");
  }, [memoryType]);

  useEffect(() => {
    if (!meta.needsEntity) return;
    if (entityQuery.trim().length < 2) {
      setEntityOptions([]);
      return;
    }
    setLoading(true);
    const path = memoryType === "ATHLETE"
      ? `/athletes?q=${encodeURIComponent(entityQuery)}&limit=10`
      : memoryType === "CLUB"
        ? `/clubs?q=${encodeURIComponent(entityQuery)}&limit=10`
        : `/federations?q=${encodeURIComponent(entityQuery)}&limit=10`;
    const t = setTimeout(() => {
      api.get(path)
        .then((r) => setEntityOptions(r.data?.items || []))
        .catch(() => setEntityOptions([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [entityQuery, memoryType, meta.needsEntity]);

  const canSubmit = !!memoryType && (
    !meta.needsEntity ? (meta.needsFreeText ? freeTextTitle.trim().length >= 3 : true)
    : !!selectedEntity
  );

  const submit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      const payload = {
        memory_type: memoryType,
        entity_type: meta.entityType,
        entity_id: meta.needsEntity ? (selectedEntity.id || selectedEntity.slug) : null,
        period_year: Number(periodYear) || null,
        // V11.21 — Pour HISTORICAL_EVENT, on transmet le sujet libre via `context`,
        // le backend l'utilise comme entity_name_snapshot dans le snapshot.
        context: meta.needsFreeText ? freeTextTitle.trim() : undefined,
      };
      const res = await api.post("/admin/memory-books/requests", payload);
      toast.success("Demande créée. Construction du snapshot…");
      onCreated?.(res.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de créer la demande");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="admin-memorybooks-new-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau livret</DialogTitle>
          <DialogDescription>
            Choisis le type de livret et l'entité cible. L'IA lira les URLs citées dans la fiche pour enrichir la rédaction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium">Type de livret</label>
            <Select value={memoryType} onValueChange={setMemoryType}>
              <SelectTrigger data-testid="new-book-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MEMORY_TYPES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {meta.needsEntity && (
            <div>
              <label className="text-xs font-medium">
                Rechercher {meta.label.toLowerCase()}
              </label>
              <Input
                data-testid="new-book-entity-search"
                placeholder={`Nom / slug de ${meta.label.toLowerCase()}…`}
                value={entityQuery}
                onChange={(e) => { setEntityQuery(e.target.value); setSelectedEntity(null); }}
                className="mt-1"
              />
              {loading && (
                <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Recherche…
                </div>
              )}
              {entityOptions.length > 0 && !selectedEntity && (
                <div className="mt-1 border rounded-md max-h-48 overflow-y-auto">
                  {entityOptions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => { setSelectedEntity(o); setEntityQuery(o.full_name || o.name); setEntityOptions([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-b-0"
                      data-testid={`new-book-entity-option-${o.id}`}
                    >
                      <div className="font-medium">{o.full_name || o.name}</div>
                      {(o.main_discipline || o.island) && (
                        <div className="text-[11px] text-muted-foreground">
                          {[o.main_discipline, o.island].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {selectedEntity && (
                <div className="mt-1 text-xs px-2 py-1.5 bg-[hsl(var(--aito-lagoon))]/10 border border-[hsl(var(--aito-lagoon))]/30 rounded" data-testid="new-book-selected-entity">
                  ✓ {selectedEntity.full_name || selectedEntity.name}
                </div>
              )}
            </div>
          )}

          {/* V11.21 — Champ texte libre pour HISTORICAL_EVENT (fait historique sportif) */}
          {meta.needsFreeText && (
            <div>
              <label className="text-xs font-medium">Sujet du fait historique</label>
              <Input
                data-testid="new-book-freetext-title"
                placeholder="Ex: Michel Bourez à Teahupo'o JO 2024"
                value={freeTextTitle}
                onChange={(e) => setFreeTextTitle(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                L'IA cherchera sur tahiti-infos, tntv, polynésie 1ère, radio1 et sport-tahiti pour construire le livret.
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium">Année (période)</label>
            <Input
              data-testid="new-book-period-year"
              type="number"
              value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value)}
              min="2000"
              max="2030"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || creating}
            data-testid="new-book-submit"
            className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
          >
            {creating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Création…</> : "Créer le livret"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

}
